import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ImageOff, Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@cms/ui/components/dialog";
import { Button } from "@cms/ui/components/button";
import {
  fetchMedia,
  isImageMedia,
  mediaDisplayName,
  mediaUrl,
  uploadMedia,
  type MediaItem,
} from "../lib/media";
import type { ApiClient } from "../lib/api";

/**
 * Reusable image chooser: browse the Media library or upload new files, used
 * anywhere the portal offers an image (logo, section backgrounds, content
 * images, menu-item photos, galleries). Sources are tabs so future options
 * (e.g. AI generation) slot in without changing call sites.
 */
export function MediaPickerDialog({
  open,
  onOpenChange,
  api,
  multiple = false,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: ApiClient | null;
  /** Allow choosing several items (e.g. adding photos to a gallery). */
  multiple?: boolean;
  onSelect: (items: MediaItem[]) => void;
}) {
  const [tab, setTab] = useState<"library" | "upload">("library");
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [assetsBaseUrl, setAssetsBaseUrl] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !api) return;
    setTab("library");
    setSelected([]);
    setError("");
    setItems(null);
    fetchMedia(api)
      .then((data) => {
        setItems(data.media.filter(isImageMedia));
        setAssetsBaseUrl(data.assetsBaseUrl);
      })
      .catch((err) => setError(err.message));
  }, [open, api]);

  const toggle = (item: MediaItem) => {
    setSelected((prev) => {
      if (prev.includes(item.id)) return prev.filter((id) => id !== item.id);
      return multiple ? [...prev, item.id] : [item.id];
    });
  };

  const confirmSelection = () => {
    const chosen = (items ?? [])
      .filter((item) => selected.includes(item.id))
      // Attach a resolvable URL so consumers can preview without the base URL.
      .map((item) => ({
        ...item,
        url: item.url ?? mediaUrl(item, assetsBaseUrl),
        thumbUrl: item.thumbUrl ?? mediaUrl(item, assetsBaseUrl, { thumb: true }),
      }));
    if (chosen.length) onSelect(chosen);
  };

  const uploadFiles = async (files: File[]) => {
    if (!api || !files.length) return;
    setUploading(true);
    setError("");
    try {
      const uploaded: MediaItem[] = [];
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : "Uploading…");
        uploaded.push(await uploadMedia(api, files[i]));
      }
      onSelect(multiple ? uploaded : uploaded.slice(0, 1));
    } catch (err) {
      setError(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => !uploading && onOpenChange(next)}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{multiple ? "Choose images" : "Choose an image"}</DialogTitle>
          <DialogDescription>
            Reuse something from your library, or upload new {multiple ? "images" : "an image"}.
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex items-center gap-1 self-start rounded-lg bg-muted p-1">
          {([
            { id: "library" as const, label: "Library" },
            { id: "upload" as const, label: "Upload" },
          ]).map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {tab === "library" && (
          <div className="min-h-48">
            {!items && !error && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {items && items.length === 0 && (
              <p className="py-16 text-center text-sm text-muted-foreground">
                No images in your library yet — use the Upload tab to add some.
              </p>
            )}
            {items && items.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                {items.map((item) => {
                  const src = mediaUrl(item, assetsBaseUrl, { thumb: true });
                  const isSelected = selected.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggle(item)}
                      className={`group relative overflow-hidden rounded-md border text-left transition-shadow ${
                        isSelected ? "ring-2 ring-primary" : "hover:shadow-md"
                      }`}
                    >
                      <div className="aspect-square bg-muted">
                        {src ? (
                          <img src={src} alt={item.alt || ""} loading="lazy" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            <ImageOff className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      )}
                      <p
                        className={`truncate px-1.5 py-1 text-xs ${
                          item.title ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {mediaDisplayName(item)}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "upload" && (
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="flex min-h-48 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-muted-foreground/30 p-8 text-center hover:bg-muted/50"
          >
            {uploading
              ? <Loader2 className="mb-2 h-6 w-6 animate-spin text-muted-foreground" />
              : <Upload className="mb-2 h-6 w-6 text-muted-foreground" />}
            <p className="text-sm text-muted-foreground">
              {uploadProgress || `Click to upload ${multiple ? "images" : "an image"}`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Photos are resized automatically so your site stays fast.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,.svg"
              multiple={multiple}
              className="hidden"
              onChange={(e) => {
                void uploadFiles(Array.from(e.target.files ?? []));
              }}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          {tab === "library" && (
            <Button onClick={confirmSelection} disabled={!selected.length}>
              {multiple && selected.length > 1 ? `Use ${selected.length} images` : "Use image"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Imperative bridge for non-React consumers (Editor.js tools) and terse call
 * sites: `pickImage()` opens the dialog and resolves with the chosen item, or
 * null when dismissed. Render `pickerElement` once in the host component.
 */
export function useMediaPicker(api: ApiClient | null): {
  pickImage: () => Promise<MediaItem | null>;
  pickerElement: React.ReactNode;
} {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<((item: MediaItem | null) => void) | null>(null);

  const settle = useCallback((item: MediaItem | null) => {
    resolverRef.current?.(item);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const pickImage = useCallback(() => {
    return new Promise<MediaItem | null>((resolve) => {
      // A pick already in flight resolves as cancelled.
      resolverRef.current?.(null);
      resolverRef.current = resolve;
      setOpen(true);
    });
  }, []);

  return {
    pickImage,
    pickerElement: (
      <MediaPickerDialog
        open={open}
        onOpenChange={(next) => { if (!next) settle(null); }}
        api={api}
        onSelect={(items) => settle(items[0] ?? null)}
      />
    ),
  };
}
